import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Search
} from '@mui/icons-material';
import DashboardService from '../../services/dashboard';
import DateInterval from '../../components/calendar/DateInterval';

const ReplenishmentAnalysis = ({ storeId }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await DashboardService.getReplenishmentAnalysis(storeId, {
        ...dateRange,
        limit: 50
      });
      setData(result);
      setFilteredData(result);
    } catch (err) {
      setError('Error al cargar el análisis de reposición');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = data.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchTerm, data]);

  const handleDateChange = (start, end) => {
    if (start && end) {
      setDateRange({
        date_from: start.format('YYYY-MM-DD'),
        date_to: end.format('YYYY-MM-DD')
      });
    }
  };

  const getPriceVariationChip = (variation) => {
    const varValue = variation || 0;
    if (varValue > 10) {
      return <Chip label={`+${varValue.toFixed(1)}%`} size="small" color="error" icon={<TrendingUp />} />;
    } else if (varValue < -10) {
      return <Chip label={`${varValue.toFixed(1)}%`} size="small" color="success" icon={<TrendingDown />} />;
    } else {
      return <Chip label={`${varValue.toFixed(1)}%`} size="small" color="warning" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Date Filter and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <DateInterval
            startDate={dateRange.date_from}
            endDate={dateRange.date_to}
            onChange={handleDateChange}
          />
        </Box>
        <TextField
          placeholder="Buscar producto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Cards */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 200, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Total Recompras
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {filteredData.reduce((sum, item) => sum + item.total_replenishments, 0)}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 200, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Inversión Total
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(
              filteredData.reduce((sum, item) => sum + item.total_invested, 0)
            )}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 200, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Productos
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {filteredData.length}
          </Typography>
        </Paper>
      </Box>

      {/* Replenishment Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Análisis de Reposición de Productos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Productos más recomprados y su evolución de precios
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Recompras</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Cantidad Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Inversión</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Precio Promedio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Último Precio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Variación</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Frecuencia (días)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Stock Actual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((product) => (
                  <TableRow key={product.article_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku} • {product.category_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={product.total_replenishments}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{product.total_quantity_purchased}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.total_invested)}
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.average_purchase_price)}
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.last_purchase_price)}
                    </TableCell>
                    <TableCell align="right">
                      {getPriceVariationChip(product.price_variation)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={(product.days_between_purchases || 0).toFixed(0)}
                        size="small"
                        color={(product.days_between_purchases || 0) < 30 ? 'success' : (product.days_between_purchases || 0) < 60 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={product.current_stock}
                        size="small"
                        color={product.current_stock < 10 ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ReplenishmentAnalysis;
